import {
  ConnectorRail,
  EmptyStatePage,
  GlassCard,
  Glyph,
  makeEntrance,
  usePrefersReducedMotion,
} from "@/components/application/empty-state-page";

const ALERT_ICON =
  "M12 9v4m0 4h.01M10.615 3.892 2.39 18.098c-.456.788-.684 1.182-.65 1.506a1 1 0 0 0 .406.705c.263.191.718.191 1.629.191h16.45c.91 0 1.365 0 1.628-.191a1 1 0 0 0 .407-.705c.034-.324-.195-.718-.65-1.506L13.383 3.892c-.454-.785-.681-1.178-.978-1.31a1 1 0 0 0-.813 0c-.296.132-.523.525-.978 1.31Z";
const BLOCK_USER_ICON =
  "m16.5 16 5 5m0-5-5 5M12 15.5H7.5c-1.396 0-2.093 0-2.661.172a4 4 0 0 0-2.667 2.667C2 18.907 2 19.604 2 21M14.5 7.5a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z";

// The final card lands with a spring rather than the standard glass-in ease.
const SPRING_POP = "glass-in 620ms cubic-bezier(0.34, 1.56, 0.64, 1) 0.89s both";

type BlockedCreatorsEmptyStateProps = {
  onBrowseCreators: () => void;
  onCreateRule: () => void;
};

export function BlockedCreatorsEmptyState({ onBrowseCreators, onCreateRule }: BlockedCreatorsEmptyStateProps) {
  const reduceMotion = usePrefersReducedMotion();
  const entrance = makeEntrance(reduceMotion);

  return (
    <EmptyStatePage
      eyebrow="Blocked Creators"
      headline="Block once, and it carries through everything"
      subline="A blocked creator drops out of every collab and message from that moment, including sequences already running. Nothing to unpick by hand."
      measureCh={44}
      portraitSrc="/empty-states/portrait-blocked-creators.webp"
      portraitMaskLeft="22%"
      actionsDelaySeconds={1.07}
      primaryAction={{ label: "Create a Rule to Block Automatically", onClick: onCreateRule }}
      secondaryLink={{ label: "Browse Creators to Block", onClick: onBrowseCreators }}
      device={
        <div className="flex w-fit max-w-[540px] flex-col items-start">
          <div className="flex max-w-full justify-start">
            <div className="flex flex-col items-start">
              <GlassCard
                radius="full"
                className="backdrop-blur-3xl"
                style={{ animation: entrance.glassIn(0.35) }}
                overlayStyle={{ animation: entrance.fadeIn(0.35) }}
              >
                <div className="flex items-start gap-4 px-5 py-4" style={{ animation: entrance.fadeInUp(0.35) }}>
                  <span
                    aria-hidden="true"
                    className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/12 text-brand-100 backdrop-blur-sm"
                  >
                    <Glyph d={ALERT_ICON} />
                  </span>
                  <div className="flex flex-col gap-0.5 pt-1">
                    <p className="text-base font-semibold text-white">
                      This creator has 10 violations of critical safety risks
                    </p>
                  </div>
                </div>
              </GlassCard>
              <ConnectorRail delaySeconds={0.57} reduceMotion={reduceMotion} className="ml-[38px]" />
            </div>
          </div>

          <div className="flex max-w-full justify-start">
            <div className="flex flex-col items-start">
              <GlassCard
                radius="2xl"
                className="backdrop-blur-3xl"
                style={{ animation: reduceMotion ? undefined : SPRING_POP }}
                overlayStyle={{ animation: entrance.fadeIn(0.77) }}
              >
                <div className="flex items-start gap-4 px-5 py-4" style={{ animation: entrance.fadeInUp(0.77) }}>
                  <span
                    aria-hidden="true"
                    className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-200 text-unbox-dark-brand-start"
                  >
                    <Glyph d={BLOCK_USER_ICON} />
                  </span>
                  <div className="flex flex-col gap-0.5 pt-1">
                    <p className="text-base font-semibold text-white">Block Creator?</p>
                  </div>
                </div>
              </GlassCard>
            </div>
          </div>
        </div>
      }
    />
  );
}
